import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

interface FeatureCardProps {
  icon: string;
  iconColor: string;
  title: string;
  description: string;
  delay: string;
  isVisible: boolean;
  animType?: string;
}

function FeatureCard({ icon, iconColor, title, description, delay, isVisible, animType = 'scroll-hidden-blur' }: FeatureCardProps) {
  return (
    <div
      className={`${animType} ${isVisible ? 'scroll-visible' : ''} ${delay} card-lift p-8 rounded-2xl bg-white border border-gray-100 group cursor-default hover:border-brand-purple/20`}
    >
      <div className={`w-14 h-14 flex items-center justify-center rounded-xl mb-6 ${iconColor} group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 wiggle-hover`}>
        <i className={`${icon} text-2xl`} />
      </div>
      <h3 className="font-heading text-xl font-bold text-brand-blue-dark mb-3 group-hover:text-brand-purple transition-colors duration-300">
        {title}
      </h3>
      <p className="font-body text-gray-500 text-sm leading-relaxed">
        {description}
      </p>
      <div className="h-0.5 w-0 bg-brand-yellow mt-6 group-hover:w-full transition-all duration-500" />
    </div>
  );
}

const features = [
  {
    icon: 'ri-music-2-line',
    iconColor: 'bg-brand-purple/10 text-brand-purple',
    title: 'Multidisciplinary Arts',
    description: 'From music and visual arts to performance and media production, we expose students to a wide range of creative disciplines — building versatile artists ready for any stage.',
  },
  {
    icon: 'ri-heart-line',
    iconColor: 'bg-brand-yellow/20 text-brand-yellow-dark',
    title: 'Inclusive Learning',
    description: 'Every student deserves a seat at the table. Our programs are designed to meet individuals where they are — embracing diverse learning styles, backgrounds, and abilities.',
  },
  {
    icon: 'ri-seedling-line',
    iconColor: 'bg-brand-blue/10 text-brand-blue-mid',
    title: 'Growth for All Ages',
    description: 'Whether you\'re 5 or 55, there\'s a place for you here. We offer age-appropriate pathways that nurture creativity, confidence, and real-world skills at every life stage.',
  },
];

export default function FeaturesSection() {
  const { ref: titleRef, isVisible: titleVisible } = useScrollAnimation({ threshold: 0.2 });
  const { ref: gridRef, isVisible: gridVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <section className="py-28 bg-gradient-to-br from-[#EEF2FF] via-[#F0F4FF] to-[#F5F3FF]">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-8">
        {/* Title */}
        <div ref={titleRef} className="text-center mb-16">
          <span
            className={`scroll-hidden-pop ${titleVisible ? 'scroll-visible' : ''} inline-block px-5 py-2 rounded-full bg-brand-purple/10 text-brand-purple font-body text-xs font-semibold uppercase tracking-widest mb-5`}
          >
            Our Foundation
          </span>
          <h2
            className={`scroll-hidden ${titleVisible ? 'scroll-visible' : ''} delay-200 font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-brand-blue-dark mb-5`}
          >
            Where Creativity
            <span className="gradient-text"> Meets Excellence</span>
          </h2>
          <p
            className={`scroll-hidden-blur ${titleVisible ? 'scroll-visible' : ''} delay-300 font-body text-gray-500 text-lg max-w-2xl mx-auto leading-relaxed`}
          >
            We provide a space where every student is given the tools to succeed through structured mentorship and inclusive arts education.
          </p>
        </div>

        {/* Grid */}
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feat, idx) => (
            <FeatureCard
              key={feat.title}
              {...feat}
              delay={`delay-${(idx % 3) * 150 + 100}`}
              isVisible={gridVisible}
              animType={idx === 1 ? 'scroll-hidden-scale-blur' : 'scroll-hidden-blur'}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
